'use client';

const STORAGE_KEY = 'auth_email_attribution';
const TRACKED_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

type SearchParamsLike = {
    get(name: string): string | null;
};

export type EmailAttribution = Partial<Record<(typeof TRACKED_KEYS)[number], string>>;

export function readEmailAttribution(searchParams: SearchParamsLike): EmailAttribution | null {
    const attribution = TRACKED_KEYS.reduce<EmailAttribution>((acc, key) => {
        const value = searchParams.get(key);
        if (value) {
            acc[key] = value;
        }

        return acc;
    }, {});

    if (Object.keys(attribution).length === 0) {
        return null;
    }

    if (attribution.utm_source !== 'email' && attribution.utm_medium !== 'transactional' && !attribution.utm_campaign) {
        return null;
    }

    return attribution;
}

export function persistEmailAttribution(searchParams: SearchParamsLike): EmailAttribution | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const attribution = readEmailAttribution(searchParams);
    if (!attribution) {
        return null;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
}

export function getStoredEmailAttribution(): EmailAttribution | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
        return null;
    }

    try {
        return JSON.parse(stored) as EmailAttribution;
    } catch {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

export function clearStoredEmailAttribution() {
    if (typeof window !== 'undefined') {
        sessionStorage.removeItem(STORAGE_KEY);
    }
}

export function toAnalyticsEventParams(attribution: EmailAttribution): Record<string, string> {
    const params: Record<string, string> = {};

    if (attribution.utm_source) {
        params.email_source = attribution.utm_source;
    }
    if (attribution.utm_medium) {
        params.email_medium = attribution.utm_medium;
    }
    if (attribution.utm_campaign) {
        params.email_campaign = attribution.utm_campaign;
    }
    if (attribution.utm_content) {
        params.email_content = attribution.utm_content;
    }

    return params;
}
